// Svelte-Store-Wrapper um search-learn.ts. Verbindet das pure
// Lern-Modul mit dem prefs-Store (Server-SSOT + WS-Live-Sync) und
// macht es fuer NavSearchPanel reaktiv konsumierbar.
//
// Zwei Pref-Keys:
//   search_learn         -- serialisierter State (queries + global).
//                           Wird throttled geschrieben (2 s nach letzter
//                           Auswahl), damit nicht jeder Klick ein PATCH
//                           ausloest.
//   search_learn_config  -- LearnConfig-Overrides (decay, bonusCap, ...).
//                           Wird direkt geschrieben (Settings-Slider
//                           soll sofortige Wirkung haben).
//
// Reaktivitaet: ein _tick-$state wird bei jeder Aenderung inkrementiert.
// Konsumenten (NavSearchPanel) referenzieren ihn implizit ueber die
// exportierten Reader-Funktionen -- damit re-rankt $derived sauber.
//
// WS-Sync: subscribePref liefert sowohl initiale Cache-Hydratisierung
// als auch Server-Pushes. Wir bauen den Lern-State bei jedem Push neu;
// im Single-Tab-Fall ist das ein No-Op (identisches JSON), im Multi-
// Tab-Fall sieht der zweite Tab die Aenderungen des ersten nach ~2 s.

import {
  createSearchLearn,
  type LearnConfig,
  type LearnState,
  type SearchLearn,
} from "./search-learn";
import { getPref, setPref, subscribePref } from "./prefs.svelte";

const STATE_KEY = "search_learn";
const CONFIG_KEY = "search_learn_config";
const SAVE_DEBOUNCE_MS = 2000;

let _learn: SearchLearn = createSearchLearn();
let _tick = $state(0);
let _started = false;
let _saveTimer: ReturnType<typeof setTimeout> | undefined;
// Wenn wir gerade selbst persistieren, ignorieren wir den eigenen
// applyLocalChange-Echo (setPref ruft applyLocalChange optimistic auf,
// noch bevor der Server antwortet). Sonst wuerde der Save den State
// neu hydraten und in-flight clicks verlieren.
let _savingSnapshot: string | undefined;

function scheduleSave(): void {
  if (_saveTimer) return;
  _saveTimer = setTimeout(() => {
    _saveTimer = undefined;
    if (!_learn.isDirty()) return;
    const snap = _learn.serialize();
    _learn.clearDirty();
    _savingSnapshot = JSON.stringify(snap);
    setPref(STATE_KEY, snap);
    // Echo-Suppression nur kurz halten -- nach einem Tick ist der
    // applyLocalChange-Listener durchgelaufen, dann kann der naechste
    // echte Server-Push wieder normal hydrieren.
    setTimeout(() => {
      _savingSnapshot = undefined;
    }, 0);
  }, SAVE_DEBOUNCE_MS);
}

export function startSearchLearn(): void {
  if (_started) return;
  _started = true;

  subscribePref(STATE_KEY, (value) => {
    // Echo-Suppression: ist der Push identisch zu dem, was wir gerade
    // selbst geschrieben haben, ignorieren. Sonst wuerde ein gleichzeitig
    // laufender Save unsere dirty in-memory Aenderungen verwerfen.
    if (_savingSnapshot && JSON.stringify(value) === _savingSnapshot) return;
    // Wenn der lokale State dirty ist (Klick wartet noch auf Persist),
    // nicht ueberschreiben -- der naechste scheduleSave-Tick spielt
    // unseren Stand hoch und der Server-Push gewinnt erst danach.
    if (_learn.isDirty()) return;
    const cfg = getPref<Partial<LearnConfig>>(CONFIG_KEY);
    _learn = createSearchLearn(value, cfg);
    _tick++;
  });

  subscribePref(CONFIG_KEY, (value) => {
    _learn.setConfig((value as Partial<LearnConfig>) ?? {});
    _tick++;
    // setConfig kann State trimmen (kleinere Limits) -> dirty + save.
    if (_learn.isDirty()) scheduleSave();
  });
}

export function recordSelection(query: string, itemId: string): void {
  if (!_started) return;
  _learn.record(query, itemId);
  _tick++;
  scheduleSave();
}

// bonusFor / frecentIds lesen den Tick mit, damit $derived-Konsumer
// bei Aenderungen sauber re-rankt. Reactive Trickle ueber den $state-
// Getter funktioniert nur, wenn der Read im selben Effect-/Derived-
// Frame passiert -- ein leichter "void _tick" reicht.
export function bonusFor(query: string, itemId: string): number {
  void _tick;
  return _learn.bonus(query, itemId);
}

export function frecentIds(n: number): string[] {
  void _tick;
  return _learn.topFrecent(n);
}

export function getLearnConfig(): LearnConfig {
  void _tick;
  return _learn.getConfig();
}

// getLearnState liefert eine defensive Kopie des aktuellen Lern-States.
// Konsumiert _tick, damit $derived-Reader (z.B. die Settings-Tabellen)
// bei jeder neuen Auswahl oder WS-Sync re-rendert.
export function getLearnState(): LearnState {
  void _tick;
  return _learn.serialize();
}

export function setLearnConfig(partial: Partial<LearnConfig>): void {
  // Optimistic local + Server-PATCH: subscribePref-Callback feuert
  // sofort durch applyLocalChange, womit _learn.setConfig synchron
  // angewandt wird und das Re-Rank sofort eintritt.
  const next: LearnConfig = { ..._learn.getConfig(), ...partial };
  setPref(CONFIG_KEY, next);
}

// resetSearchLearn loescht den gelernten State (Settings -> "Lerndaten
// zuruecksetzen"). Config bleibt erhalten -- der User wollte den
// Verlauf zuruecksetzen, nicht seine eingestellten Werte.
export function resetSearchLearn(): void {
  setPref(STATE_KEY, null);
  _learn = createSearchLearn(null, getPref<Partial<LearnConfig>>(CONFIG_KEY));
  _tick++;
}
